// Carimbo na foto (pedido 2026-10-08, vistoria: "nas imagens quero que fique
// o endereço, horário e data"). Desenha uma faixa escura no rodapé da
// própria imagem com as linhas de texto — fica gravado no arquivo, então
// vale como comprovante mesmo baixado/encaminhado fora do sistema.
// Também redimensiona (mesmo limite do compress-image) e devolve JPEG.
// Se o navegador não conseguir decodificar (HEIC etc.), devolve null e o
// chamador segue com o arquivo original.
// 1280 px / 0.72 (2026-10-23, "fotos demoram muito pra enviar"): só é usado
// nas fotos da vistoria; 1600/0.85 dava 600–900 KB por foto no iPhone e
// cada uma levava ~15 s pra subir. Assim fica ~200–300 KB, texto do
// carimbo continua legível.
const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.72;

export async function stampImageFile(file: File, lines: string[]): Promise<File | null> {
  const text = lines.map((l) => l.trim()).filter(Boolean);
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    if (text.length > 0) {
      const fontSize = Math.max(14, Math.round(Math.min(width, height) * 0.032));
      const pad = Math.round(fontSize * 0.7);
      const lineHeight = Math.round(fontSize * 1.3);
      ctx.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      const wrapped = text.flatMap((l) => wrapLine(ctx, l, width - pad * 2));
      const barHeight = wrapped.length * lineHeight + pad * 2;
      ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
      ctx.fillRect(0, height - barHeight, width, barHeight);
      ctx.fillStyle = "#ffffff";
      ctx.textBaseline = "top";
      ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
      ctx.shadowBlur = 2;
      wrapped.forEach((l, n) => {
        ctx.fillText(l, pad, height - barHeight + pad + n * lineHeight + (lineHeight - fontSize) / 2);
      });
    }

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) return null;
    return new File([blob], file.name.replace(/\.[^./\\]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return null;
  }
}

// Quebra por palavra; no máximo 3 linhas por texto (endereço muito longo
// não cobre a foto inteira).
function wrapLine(ctx: CanvasRenderingContext2D, line: string, maxWidth: number): string[] {
  const out: string[] = [];
  let cur = "";
  for (const word of line.split(/\s+/)) {
    const next = cur ? `${cur} ${word}` : word;
    if (cur && ctx.measureText(next).width > maxWidth) {
      out.push(cur);
      cur = word;
    } else {
      cur = next;
    }
  }
  if (cur) out.push(cur);
  if (out.length > 3) {
    const kept = out.slice(0, 3);
    kept[2] = `${kept[2]}…`;
    return kept;
  }
  return out;
}
